/** Tiny SWR query cache for the storefront (~TanStack-lite, framework-agnostic core).
 *  The Vue composable adapter (useQuery) lands with the storefront in F1. */
export class QueryCache {
    maxEntries;
    entries = new Map();
    inflight = new Map();
    constructor(maxEntries = 100) {
        this.maxEntries = maxEntries;
    }
    /** SWR fetch: fresh-cache hit → sync value; stale + swr → stale value now, revalidate in background;
     *  otherwise fetch (deduped across concurrent callers). */
    async fetch(key, fn, opts = {}) {
        const ttl = opts.ttl ?? 30_000;
        const entry = this.entries.get(key);
        const age = entry ? Date.now() - entry.storedAt : Infinity;
        if (entry && age < ttl)
            return entry.value;
        if (entry && opts.swr) {
            void this.revalidate(key, fn);
            return entry.value;
        }
        return this.revalidate(key, fn);
    }
    revalidate(key, fn) {
        const existing = this.inflight.get(key);
        if (existing)
            return existing;
        const p = fn()
            .then(value => {
            this.set(key, value);
            return value;
        })
            .finally(() => this.inflight.delete(key));
        this.inflight.set(key, p);
        return p;
    }
    get(key) {
        return this.entries.get(key)?.value;
    }
    set(key, value) {
        // naive LRU: Map preserves insertion order; re-insert on set
        this.entries.delete(key);
        this.entries.set(key, { value, storedAt: Date.now() });
        if (this.entries.size > this.maxEntries) {
            const oldest = this.entries.keys().next().value;
            if (oldest !== undefined)
                this.entries.delete(oldest);
        }
    }
    invalidate(prefix) {
        for (const key of this.entries.keys()) {
            if (key.startsWith(prefix))
                this.entries.delete(key);
        }
    }
    clear() {
        this.entries.clear();
    }
}
//# sourceMappingURL=index.js.map